import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSettings, Button, Checkbox, Icon } from '@salesforce/design-system-react';
import Dropdown from '@salesforce/design-system-react/components/menu-dropdown'; 
import { IOrg, IOrgPortalEntry } from 'interfaces/IOrg.interface';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import CQConfirmationModal from 'components/cq-confirmation-modal';
import { parseTagNames } from 'common/tag.utils';
import OrgInfoModal from 'components/cq-org-info-modal';
import { CQAppConstant } from 'app-constants';

/**
 * Props for the OrgRow component.
 */
interface OrgRowProps {
    /**
     * The index of the org row 
     */
    index: number;
    
    /**
     * Current page number
     */
    pageNo: number;
    
    /**
     * The org portal entry data.
     */
    entry: IOrgPortalEntry;
    /**
     * The current username.
     */
    currentUsername: string;
    /**
     * Callback to refresh the org data.
     */
    onRefresh: () => void;
    onNotify?: (message: string, variant: typeof CQAppConstant.ALERT_VARIANT.SUCCESS | typeof CQAppConstant.ALERT_VARIANT.ERROR) => void;
    /**
     * Whether the user can register/delete orgs.
     */
    canRegister?: boolean;
    /**
     * Optional callback to remove orgs from selection state after deletion.
     */
    onRemoveFromSelection?: (orgIds: string[]) => void;
    /**
     * Optional flag to enable org selection.
     */
    selectable?: boolean;
    selectedOrgIds?: Map<string, { orgName: string; orgType: string }>;
    onSelect?: (orgId: string, checked: boolean) => void;
    orgTypeFilter?: typeof CQAppConstant.FILTER_MODE.ALL | typeof CQAppConstant.ORG_TYPE.PRODUCTION | typeof CQAppConstant.ORG_TYPE.SANDBOX;
}

/**
 * Renders an auth status badge for an org.
 *
 * Uses SLDS badge styles based on `authStatus`.
 */
function AuthStatusBadge({ status }: { status: IOrg['authStatus'] }) {
    const map: Record<IOrg['authStatus'], string> = {
        AUTHENTICATED: CQAppConstant.AUTH_STATUS_BADGE_CLASSES.AUTHENTICATED,
        UNAUTHENTICATED: CQAppConstant.AUTH_STATUS_BADGE_CLASSES.UNAUTHENTICATED,
        EXPIRED: CQAppConstant.AUTH_STATUS_BADGE_CLASSES.EXPIRED,
    };
    return <span className={map[status]}>{status}</span>;
}

/**
 * Renders the current deployment (last upgrade run) status badge for an org.
 *
 * Returns null when the org has no recorded upgrade run yet, or when the
 * last run succeeded — success cases do not show a badge.
 */
function DeploymentStatusBadge({ org }: { org: IOrg }) {
    if (!org.lastUpgradeStatus || org.lastUpgradeStatus === CQAppConstant.UPGRADE_STATUS.SUCCESS) return null;
    const label = org.lastUpgradeStatus.replace(/_/g, ' ');
    return (
        <span className={`${CQAppConstant.DEPLOYMENT_STATUS_BADGE_CLASSES[org.lastUpgradeStatus]} slds-m-left_x-small`}>
            {label}
        </span>
    );
}

/**
 * Renders a single org entry row (parent org) and optionally its child sandboxes.
 *
 * Supports:
 * - expanding/collapsing child sandboxes
 * - syncing installed package data per org
 * - selecting orgs for bulk scheduling (checkboxes)
 * - opening an org details modal
 */
function OrgRow({
    index,
    pageNo,
    entry,
    currentUsername,
    onRefresh,
    selectable,
    selectedOrgIds,
    onSelect,
    onNotify,
    canRegister,
    onRemoveFromSelection,
    orgTypeFilter = 'all'
}: OrgRowProps) {
    const { t } = useTranslation();
    const { org, childSandboxes } = entry;
    const [isExpanded, setIsExpanded] = useState(false);
    const [installedPackagesByOrgId, setInstalledPackagesByOrgId] = useState<Record<string, Record<string, string> | null>>(() => {
        const initial: Record<string, Record<string, string> | null> = {
            [org.orgId]: org.installedPackages ?? null,
        };
        childSandboxes.forEach(sandbox => {
            initial[sandbox.orgId] = sandbox.installedPackages ?? null;
        });
        return initial;
    });
    const [syncLoadingByOrgId, setSyncLoadingByOrgId] = useState<Record<string, boolean>>({});
    const [syncErrorByOrgId, setSyncErrorByOrgId] = useState<Record<string, string>>({});
    const [orgInfoOpen, setOrgInfoOpen] = useState(false);
    const [orgInfoTarget, setOrgInfoTarget] = useState<IOrg | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<IOrg | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const deleteOptions = canRegister ? [{ id: CQAppConstant.PUSH_PORTAL_UI.ROW_ACTIONS.DELETE, label: t('push_portal_delete_org') }] : [];
    const displayIndex = (pageNo - 1) * CQAppConstant.ORG_LIST_PAGE_SIZE + index + 1;

    // Sync installed packages state when entry changes (pagination/refresh)
    useEffect(() => {
        const updated: Record<string, Record<string, string> | null> = {
            [org.orgId]: org.installedPackages ?? null,
        };
        childSandboxes.forEach(sandbox => {
            updated[sandbox.orgId] = sandbox.installedPackages ?? null;
        });
        setInstalledPackagesByOrgId(updated);
    }, [org.orgId, org.installedPackages, childSandboxes]);

    /**
     * Sync installed packages for an org and update local row state.
     *
     * Calls backend `syncInstalledPackages` and updates the cached package map for the org.
     */
    async function handleSyncPackages(targetOrg: IOrg) {
        setSyncLoadingByOrgId(prev => ({ ...prev, [targetOrg.orgId]: true }));
        setSyncErrorByOrgId(prev => ({ ...prev, [targetOrg.orgId]: '' }));
        try {
            const pkgs = await PushPortalServiceProvider.syncInstalledPackages(targetOrg.orgId);
            setInstalledPackagesByOrgId(prev => ({ ...prev, [targetOrg.orgId]: pkgs }));
            onRefresh();
        } catch (err: any) {
            setSyncErrorByOrgId(prev => ({ ...prev, [targetOrg.orgId]: err?.response?.data?.error || CQAppConstant.PUSH_PORTAL_UI.TEXT.SYNC_FAILED }));
        } finally {
            setSyncLoadingByOrgId(prev => ({ ...prev, [targetOrg.orgId]: false }));
        }
    }

    /**
     * Open the delete confirmation modal for the selected org.
     */
    function openDeleteModal(targetOrg: IOrg) {
        setDeleteTarget(targetOrg);
    }

    /**
     * Execute the soft delete request and refresh list state.
     */
    async function executeDelete() {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await PushPortalServiceProvider.deleteOrg(deleteTarget.orgId);
            onNotify?.(t('push_portal_delete_success'), CQAppConstant.ALERT_VARIANT.SUCCESS);
            
            // Remove deleted org and its child sandboxes from selection state
            const orgIdsToRemove = [deleteTarget.orgId];
            if (deleteTarget.orgId === org.orgId) {
                // If deleting parent org, also remove all child sandboxes from selection
                childSandboxes.forEach(sandbox => {
                    orgIdsToRemove.push(sandbox.orgId);
                });
            }
            onRemoveFromSelection?.(orgIdsToRemove);
            
            setDeleteTarget(null);
            onRefresh();
        } catch (err: any) {
            const message = err?.response?.data?.error || t('push_portal_delete_failed');
            onNotify?.(message, CQAppConstant.ALERT_VARIANT.ERROR);
        } finally {
            setDeleteLoading(false);
        }
    }

    /**
     * Close the delete modal.
     */
    function closeDeleteModal() {
        setDeleteTarget(null);
    }

    // Toggle expand/collapse state for child sandboxes.
    function toggleExpand() {
        setIsExpanded(prev => !prev);
    }

    // Render common metadata for an org (created by + optional release name).
    function renderOrgMeta(org: IOrg) {
        const tags = renderTags(org.tagNames);
        const hasTags = Array.isArray(tags) && tags.length > 0;

        return (
            <div className='slds-var-m-left_medium'>
                <div className="slds-grid slds-wrap slds-text-body_small slds-text-color_weak ">
                    <div className="slds-grow_none slds-p-bottom_x-small slds-m-right_xx-large">
                        <strong className="slds-text-color_default">{CQAppConstant.PUSH_PORTAL_UI.TEXT.ORG_ID_LABEL}</strong>
                        <div className="slds-line-clamp_none" title={org.orgId}>{org.orgId}</div>

                    </div>
                    <div className="slds-grow_none slds-p-bottom_x-small slds-m-right_xx-large">
                        <strong className="slds-text-color_default">{CQAppConstant.PUSH_PORTAL_UI.TEXT.REGISTERED_ADMIN_USERNAME_LABEL}</strong>
                        <div className="slds-line-clamp_none" title={org.integrationUsername}>{org.integrationUsername}</div>
                    </div>
                    <div className="slds-grow_none slds-p-bottom_x-small slds-m-right_xx-large">
                        <strong className="slds-text-color_default">{CQAppConstant.PUSH_PORTAL_UI.TEXT.REGISTERED_BY_LABEL}</strong>
                        <div className="slds-line-clamp_none" title={org.createdBy}>{org.createdBy}</div>
                    </div>
                    {org.releaseName &&
                        <div className="slds-grow_none slds-p-bottom_x-small slds-m-right_xx-large">
                            <strong className="slds-text-color_default">{CQAppConstant.PUSH_PORTAL_UI.TEXT.RELEASE_LABEL}</strong>
                            <div className="slds-line-clamp_none" title={org.releaseName}>{org.releaseName}</div>
                        </div>
                    }
                </div>
                {hasTags && (
                    <div className="slds-text-body_small slds-p-top_x-small">
                        <strong className="slds-text-color_default slds-m-right_x-small">{CQAppConstant.PUSH_PORTAL_UI.TEXT.TAGS_LABEL}</strong>
                        {tags}
                    </div>
                )}
            </div>
        );
    }

    // Render org type as a filled pill.
    function renderOrgTypePill(orgType: IOrg['orgType']) {
        if (orgType !== CQAppConstant.ORG_TYPE.PRODUCTION && orgType !== CQAppConstant.ORG_TYPE.SANDBOX) return null;
        return (
            <span className="slds-badge slds-theme_inverse slds-m-right_x-small">
                {orgType.toUpperCase()}
            </span>
        );
    }

    // Render tags as badges.
    function renderTags(tagNames?: string) {
        const tags = parseTagNames(tagNames || '');
        if (tags.length === 0) return null;
        return tags.map(tag => (
            <span
                key={tag}
                className=" slds-p-horizontal_x-small slds-p-vertical_xxx-small slds-m-right_x-small"
            >
                {tag}
            </span>
        ));
    }

    // Read cached installed package map for an org id.
    function getPackagesForOrg(targetOrgId: string) {
        return installedPackagesByOrgId[targetOrgId] ?? null;
    }

    // Check if an org is currently selected (schedule checkbox).
    function isSelected(targetOrgId: string) {
        return !!selectedOrgIds?.has(targetOrgId);
    }

    // Handle selection from the actions dropdown (e.g., delete).
    function handleActionSelect(targetOrg: IOrg, data: any) {
        const selection = data?.selection ?? data?.option ?? data;
        if (selection?.id === CQAppConstant.PUSH_PORTAL_UI.ROW_ACTIONS.DELETE) {
            openDeleteModal(targetOrg);
        }
    }

    const filteredSandboxes = orgTypeFilter === CQAppConstant.ORG_TYPE.PRODUCTION
        ? []
        : childSandboxes;

    const hasChildSandboxes = childSandboxes.length > 0;

    return (
        <IconSettings iconPath="/assets/icons">
            <div className={`slds-box slds-m-bottom_small ${org.orgType === CQAppConstant.ORG_TYPE.PRODUCTION ? 'slds-theme_default' : 'slds-theme_shade'}`}>
                <div
                    className="slds-grid slds-grid_vertical-align-center slds-p-around_small slds-wrap"
                    style={{ cursor: hasChildSandboxes ? 'pointer' : 'default' }}
                    onClick={hasChildSandboxes ? toggleExpand : undefined}
                >
                    <div className="slds-col slds-size_9-of-12">
                        <div className="slds-grid slds-grid_vertical-align-center">
                            {selectable && (
                                <div className="slds-m-right_small" onClick={e => e.stopPropagation()}>
                                    <Checkbox
                                        checked={isSelected(org.orgId)}
                                        labels={{ label: '' }}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>, { checked }: { checked: boolean }) => onSelect?.(org.orgId, checked)}
                                    />
                                </div>
                            )}
                            {hasChildSandboxes && (
                                <Icon
                                    category="utility"
                                    name={isExpanded ? 'chevrondown' : 'chevronright'}
                                    size="small"
                                    className="slds-m-right_x-small"
                                />
                            )}
                            <div className="slds-grow">
                                <div className="slds-grid slds-grid_align-spread" title={org.orgName}>
                                    <strong className="slds-m-right_x-small slds-size_8-of-12">{displayIndex}. {org.orgName}</strong>
                                    <div className="slds-grid slds-p-bottom_x-small slds-size_4-of-12">
                                        {renderOrgTypePill(org.orgType)}
                                        <AuthStatusBadge status={org.authStatus} />
                                        <DeploymentStatusBadge org={org} />
                                    </div>
                                </div>
                                {renderOrgMeta(org)}
                            </div>
                        </div>
                    </div>
                    <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-12 slds-text-body_small slds-text-color_weak slds-p-top_x-small slds-medium-p-top_none">
                        {childSandboxes.length > 0 && (
                            <span>{childSandboxes.length} sandbox{childSandboxes.length !== 1 ? 'es' : ''}</span>
                        )}
                    </div>
                    <div className="slds-col slds-size_1-of-1 slds-medium-size_2-of-12 slds-text-align_right slds-p-top_x-small slds-medium-p-top_none" onClick={e => e.stopPropagation()}>
                        <Button
                            label={syncLoadingByOrgId[org.orgId] ? 'Syncing…' : 'Sync'}
                            variant="neutral"
                            iconCategory="utility"
                            iconName="refresh"
                            iconPosition="left"
                            disabled={!!syncLoadingByOrgId[org.orgId]}
                            onClick={() => handleSyncPackages(org)}
                            className="slds-m-right_x-small"
                        />
                        {syncErrorByOrgId[org.orgId] && (
                            <span className="slds-text-color_error slds-text-body_small slds-m-right_x-small" title={syncErrorByOrgId[org.orgId]}>!</span>
                        )}
                        <Button
                            label="Info"
                            variant="neutral"
                            iconCategory="utility"
                            iconName="info"
                            iconPosition="left"
                            onClick={() => { setOrgInfoTarget(org); setOrgInfoOpen(true); }}
                            className="slds-m-right_x-small"
                        />
                        {canRegister && (
                            <Dropdown
                                id={`org-actions-${org.orgId}`}
                                iconCategory="utility"
                                iconName="down"
                                iconVariant="border-filled"
                                align="right"
                                menuPosition="relative"
                                options={deleteOptions}
                                onSelect={(event: React.SyntheticEvent, data: any) => handleActionSelect(org, data)}
                            />
                        )}
                    </div>
                </div>

                {isExpanded && (
                    <div className="slds-p-horizontal_small slds-p-bottom_small">
                        {filteredSandboxes.length > 0 && (
                            <div className="slds-m-top_small">
                                <h4 className="slds-text-heading_small slds-m-bottom_x-small">
                                    Child Sandboxes ({filteredSandboxes.length})
                                </h4>
                                {filteredSandboxes.map(sandbox => (
                                    <div key={sandbox.orgId} className="slds-box slds-m-bottom_x-small slds-theme_shade">
                                        <div
                                            className="slds-grid slds-grid_vertical-align-center slds-p-around_x-small slds-wrap"
                                            style={{ cursor: 'default' }}
                                        >
                                            <div className="slds-col slds-size_1-of-1 slds-medium-size_7-of-12">
                                                {selectable && orgTypeFilter !== CQAppConstant.ORG_TYPE.PRODUCTION && (
                                                    <div className="slds-m-right_small" onClick={e => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={isSelected(sandbox.orgId)}
                                                            labels={{ label: '' }}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>, { checked }: { checked: boolean }) => onSelect?.(sandbox.orgId, checked)}
                                                        />
                                                    </div>
                                                )}
                                                <div className="slds-grow">
                                                    <div className="slds-truncate" title={sandbox.orgName}>
                                                        <strong className="slds-m-right_x-small">{sandbox.orgName}</strong>
                                                        {renderOrgTypePill(sandbox.orgType)}
                                                        <AuthStatusBadge status={sandbox.authStatus} />
                                                        <DeploymentStatusBadge org={sandbox} />
                                                    </div>
                                                    {renderOrgMeta(sandbox)}
                                                </div>
                                            </div>
                                            <div className="slds-col slds-size_1-of-1 slds-medium-size_5-of-12 slds-text-align_right slds-p-top_x-small slds-medium-p-top_none" onClick={e => e.stopPropagation()}>
                                                <Button
                                                    label={syncLoadingByOrgId[sandbox.orgId] ? 'Syncing…' : 'Sync'}
                                                    variant="neutral"
                                                    iconCategory="utility"
                                                    iconName="refresh"
                                                    iconPosition="left"
                                                    disabled={!!syncLoadingByOrgId[sandbox.orgId]}
                                                    onClick={() => handleSyncPackages(sandbox)}
                                                    className="slds-m-right_x-small"
                                                />
                                                {syncErrorByOrgId[sandbox.orgId] && (
                                                    <span className="slds-text-color_error slds-text-body_small slds-m-right_x-small" title={syncErrorByOrgId[sandbox.orgId]}>!</span>
                                                )}
                                                <Button
                                                    label="Info"
                                                    variant="neutral"
                                                    iconCategory="utility"
                                                    iconName="info"
                                                    iconPosition="left"
                                                    onClick={() => { setOrgInfoTarget(sandbox); setOrgInfoOpen(true); }}
                                                    className="slds-m-right_x-small"
                                                />
                                                {canRegister && (
                                                    <Dropdown
                                                        id={`sandbox-actions-${sandbox.orgId}`}
                                                        iconCategory="utility"
                                                        iconName="down"
                                                        iconVariant="border-filled"
                                                        align="right"
                                                        menuPosition="relative"
                                                        options={deleteOptions}
                                                        onSelect={(event: React.SyntheticEvent, data: any) => handleActionSelect(sandbox, data)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <OrgInfoModal
                isOpen={orgInfoOpen}
                org={orgInfoTarget ?? org}
                installedPackages={orgInfoTarget ? getPackagesForOrg(orgInfoTarget.orgId) : getPackagesForOrg(org.orgId)}
                onClose={() => { setOrgInfoOpen(false); setOrgInfoTarget(null); }}
                onTagsUpdated={onRefresh}
            />
            <CQConfirmationModal
                isOpen={!!deleteTarget}
                heading={t('push_portal_delete_confirm_title')}
                message={t('push_portal_delete_confirm_message', { orgName: deleteTarget?.orgName || deleteTarget?.orgId || '' })}
                confirmLabel={t('push_portal_delete_confirm_button')}
                cancelLabel={CQAppConstant.BUTTON_LABELS.CANCEL}
                confirmActionStyle="destructive"
                isActionInProgress={deleteLoading}
                progressLabel={t('push_portal_deleting')}
                onConfirm={executeDelete}
                onCancel={closeDeleteModal}
                supportingDetails={deleteTarget?.orgId === org.orgId && childSandboxes.length > 0 ? (
                    <div className="slds-m-top_small">
                        <p className="slds-text-body_small slds-text-color_weak">
                            {t('push_portal_delete_confirm_message_with_sandboxes', { count: childSandboxes.length })}
                        </p>
                        <ul className="slds-list_dotted slds-m-left_large slds-m-top_x-small">
                            {childSandboxes.map(sandbox => (
                                <li key={sandbox.orgId}>{sandbox.orgName || sandbox.orgId}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            />
        </IconSettings>
    );
}

export default OrgRow;
